import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './About.css';
import { contentAPI } from '../utils/api';
import SEO from '../components/SEO';

// Default content - always available
const defaultContent = {
  name: 'Marta Zboravska',
  photo: null,
  intro: 'I am a <strong>photographer and videographer</strong> working between Prague and Italy.',
  specialization: 'I specialize in the <strong>premium segment</strong>: wedding photography, portraits, and visual campaigns for niche brands.',
  clients: 'I shoot for people who value <strong>aesthetics, atmosphere, and depth</strong>.',
  style: 'My style is <strong>cinematic</strong>, with a touch of <strong>vintage elegance</strong> and attention to detail.',
  philosophy1: 'Not just a picture, but a <strong>mood + story + style</strong>.',
  philosophy2: 'You are not a technical performer, but a <strong>visual author and co-author</strong> of the image.',
  ctaHeading: 'Ready to create something beautiful?',
  ctaButton: 'Book Session'
};

const About = () => {
  // Start with hero visible immediately since it's at the top
  const [isVisible, setIsVisible] = useState({ hero: true });
  const [content, setContent] = useState(defaultContent);
  const sectionRefs = useRef({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await contentAPI.getSection('about');
        // Merge API data with defaults - API data takes priority
        if (data.content?.data) {
          setContent(prev => ({ ...prev, ...data.content.data }));
        } else if (data.content && typeof data.content === 'object' && !data.content.section) {
          setContent(prev => ({ ...prev, ...data.content }));
        }
      } catch (error) {
        console.error('Error fetching about content:', error);
      }
    };
    fetchContent();
  }, []);

  useEffect(() => {
    // Small delay to ensure DOM is ready, then trigger animations
    const timer = setTimeout(() => {
      setIsVisible(prev => ({ ...prev, hero: true, main: true }));
    }, 100);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({
              ...prev,
              [entry.target.dataset.section]: true,
            }));
          }
        });
      },
      { threshold: 0.05, rootMargin: '100px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <div className="about-page">
      <SEO
        title="About Prague Photographer"
        description="Marta Zborovska - professional photographer and videographer in Prague. Weddings, portraits, fashion campaigns. Cinematic style between Prague and Italy."
        path="/about"
      />
      {/* Hero Section */}
      <section className="about-hero" ref={(el) => (sectionRefs.current['hero'] = el)} data-section="hero">
        <div className="about-hero-container">
          <div className={`about-hero-content ${isVisible['hero'] ? 'visible' : ''}`}>
            <h1 className="about-name">{content.name}</h1>
            <div className="about-divider"></div>
          </div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="about-main" ref={(el) => (sectionRefs.current['main'] = el)} data-section="main">
        <div className="about-container">
          <div className={`about-layout ${content.photo ? 'has-photo' : 'no-photo'}`}>
            {content.photo && (
              <div className="about-image-section">
                <div className="about-image-wrapper">
                  <img 
                    src={content.photo} 
                    alt={content.name} 
                    className="about-image" 
                  />
                </div>
              </div>
            )}
            <div className={`about-text-section ${isVisible['main'] ? 'visible' : ''}`}>
              <p className="about-intro" dangerouslySetInnerHTML={{ __html: content.intro }} />
              <p className="about-specialization" dangerouslySetInnerHTML={{ __html: content.specialization }} />
              <p className="about-clients" dangerouslySetInnerHTML={{ __html: content.clients }} />
              <p className="about-style" dangerouslySetInnerHTML={{ __html: content.style }} />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="about-philosophy-section" ref={(el) => (sectionRefs.current['philosophy'] = el)} data-section="philosophy">
        <div className="about-container">
          <div className={`philosophy-content ${isVisible['philosophy'] ? 'visible' : ''}`}>
            <p className="philosophy-text" dangerouslySetInnerHTML={{ __html: content.philosophy1 }} />
            <p className="philosophy-text" dangerouslySetInnerHTML={{ __html: content.philosophy2 }} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="about-cta-section" ref={(el) => (sectionRefs.current['cta'] = el)} data-section="cta">
        <div className="about-container">
          <div className={`about-cta-content ${isVisible['cta'] ? 'visible' : ''}`}>
            <h2 className="cta-heading">{content.ctaHeading}</h2>
            <Link to="/booking" className="about-cta-button">{content.ctaButton}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
